import { useMediaQuery, Breakpoint, Theme } from '@mui/material';
import theme from '../../theme';

export type BreakpointValue = number | Breakpoint;
export type BreakpointArg = BreakpointValue | ((t: Theme) => BreakpointValue);

/**
 * This hook will return a boolean based on the value it gets from the argument. It will check if the screen size is smaller than what the argument was.
 * @param breakpoint Accepts either Breakpoint, number or a callback that returns a number or Breakpoint. The callback provides Theme as argument
 * @returns boolean
 */
const useScreenSmallerThan = (breakpoint: BreakpointArg) => {
    const bpoint = typeof breakpoint === 'function' ? breakpoint(theme) : breakpoint;
    return useMediaQuery((t: Theme) => t.breakpoints.down(bpoint));
};

/**
 * This hook will return a boolean based on the value it gets from the argument. It will check if the screen size is larger than what the argument was.
 * @param breakpoint Accepts either Breakpoint, number or a callback that returns a number or Breakpoint. The callback provides Theme as argument
 * @returns boolean
 */
const useScreenLargerThan = (breakpoint: BreakpointArg) => {
    const bpoint = typeof breakpoint === 'function' ? breakpoint(theme) : breakpoint;
    return useMediaQuery((t: Theme) => t.breakpoints.up(bpoint));
};

/**
 * This hook will return a boolean based on the value it gets from the argument. It will check if the screen size is inside the range of a breakpoint.
 * @param breakpoint Accepts Breakpoint (only screen sizes, px in numbers are nut supported)
 * @returns boolean
 */
const useScreenExactBreakpoint = (breakpoint: Breakpoint) => {
    return useMediaQuery((t: Theme) => t.breakpoints.only(breakpoint));
};

type StartParam =
    | BreakpointValue
    | [BreakpointValue, BreakpointValue]
    | ((t: Theme) => [BreakpointValue, BreakpointValue]);

/**
 * This hook will check the start argument and will decide if start argument is array or it's a function, it will not consider end argument and will get first two elements of the start argument and will destruct is the first element is start and the second element is end breakpoints. If the start argument is type of Breakpoint or number, then it will require the end argument.
 * @param start Argument either Breakpoint, number, an array with 2 elements (start and end values) or a callback that returns an array with two elements (start and end values) that provides Theme as argument
 * @param end A Breakpoint or a number
 * @returns boolean
 */
const useScreenBetweenSizes = (start: StartParam, end?: BreakpointValue) => {
    let args: [BreakpointValue, BreakpointValue];

    if (Array.isArray(start)) {
        args = [...start];
    } else if (typeof start === 'function') {
        args = [...start(theme)];
    } else {
        if (!end) throw new Error('Please provide ending breakpoint');
        args = [start, end];
    }

    return useMediaQuery((t: Theme) => t.breakpoints.between(args[0], args[1]));
};

export {
    useScreenSmallerThan,
    useScreenLargerThan,
    useScreenBetweenSizes,
    useScreenExactBreakpoint
};
